import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from 'motion/react';
import { useRef } from 'react';
import { Reveal, RevealWords } from './Reveal';

/**
 * Interactive hero: cut-out cakes fly in, follow the cursor (parallax), drift
 * and scale on scroll, and float idle — each with its own rhythm. The clean
 * saint-honoré is the protagonist on a saturated sage disc.
 */

type Cake = {
  src: string; alt: string; x: string; y: string; size: number; depth: number;
  scrollDy: number; scrollScale: number; fromX: number; fromY: number; fromRot: number;
  floatY: number; floatX: number; rot: [number, number, number]; dur: number;
  ease: [number, number, number, number]; delay: number;
};

const CAKES: Cake[] = [
  // protagonist — clean crostata on the sage disc (right)
  {
    src: '/images/cutout/crostata.png', alt: 'Crostata ai lamponi',
    x: '57%', y: '28%', size: 540, depth: 40, scrollDy: 70, scrollScale: 1.2,
    fromX: 480, fromY: 200, fromRot: 150,
    floatY: 26, floatX: -12, rot: [0, 2.5, 0], dur: 8, ease: [0.37, 0, 0.63, 1], delay: 0.2,
  },
  // torta ai frutti di bosco (top-left, small)
  {
    src: '/images/cutout/frutti-bosco.png', alt: 'Torta ai frutti di bosco',
    x: '2%', y: '8%', size: 260, depth: 26, scrollDy: -110, scrollScale: 0.8,
    fromX: -420, fromY: -160, fromRot: -120,
    floatY: 18, floatX: 8, rot: [0, -3.5, 0], dur: 6.4, ease: [0.45, 0, 0.55, 1], delay: 0.34,
  },
  // cherry blossom (bottom-left, small)
  {
    src: '/images/cutout/cherry-blossom.png', alt: 'Torta cherry blossom',
    x: '24%', y: '62%', size: 240, depth: 36, scrollDy: 150, scrollScale: 0.82,
    fromX: -260, fromY: 320, fromRot: 70,
    floatY: 14, floatX: 14, rot: [0, 4, 0], dur: 5.4, ease: [0.5, 0, 0.5, 1], delay: 0.48,
  },
];

function FloatingCake({ cake, reduce, px, py, progress }: {
  cake: Cake; reduce: boolean | null; px: MotionValue<number>; py: MotionValue<number>; progress: MotionValue<number>;
}) {
  const parX = useTransform(px, [-1, 1], [-cake.depth, cake.depth]);
  const parY = useTransform(py, [-1, 1], [-cake.depth, cake.depth]);
  const scrY = useTransform(progress, [0, 1], [0, cake.scrollDy]);
  const scrScale = useTransform(progress, [0, 1], [1, cake.scrollScale]);
  const scrRot = useTransform(progress, [0, 1], [0, cake.scrollDy > 0 ? 8 : -8]);
  return (
    <motion.div
      className="absolute will-change-transform"
      style={{ left: cake.x, top: cake.y, width: `min(${cake.size}px, 52vw)` }}
      initial={reduce ? { opacity: 0 } : { opacity: 0, x: cake.fromX, y: cake.fromY, rotate: cake.fromRot, scale: 0.55 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }}
      transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1], delay: cake.delay }}
    >
      <motion.div style={reduce ? undefined : { x: parX, y: parY }}>
        <motion.div style={reduce ? undefined : { y: scrY, scale: scrScale, rotate: scrRot }}>
          <motion.img
            src={cake.src} alt={cake.alt} draggable={false}
            className="w-full drop-shadow-[0_34px_46px_rgba(94,107,73,0.28)]"
            animate={reduce ? undefined : { y: [0, -cake.floatY, 0], x: [0, cake.floatX, 0], rotate: cake.rot }}
            transition={{ duration: cake.dur, repeat: Number.POSITIVE_INFINITY, ease: cake.ease, delay: 1.3 + cake.delay }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export function FloatingCakesHero() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const px = useSpring(rawX, { stiffness: 120, damping: 24, mass: 0.4 });
  const py = useSpring(rawY, { stiffness: 120, damping: 24, mass: 0.4 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  const onMove = (e: React.PointerEvent) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    rawX.set(((e.clientX - r.left) / r.width) * 2 - 1);
    rawY.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };
  const onLeave = () => { rawX.set(0); rawY.set(0); };

  return (
    <section ref={ref} onPointerMove={onMove} onPointerLeave={onLeave} className="relative h-svh w-full overflow-hidden bg-cream">
      {/* saturated shapes: protagonist pops on a solid sage disc */}
      <div aria-hidden className="absolute inset-0 overflow-hidden">
        <div className="absolute right-[-10vw] top-[12%] h-[86vw] w-[86vw] rounded-full bg-sage md:h-[48vw] md:w-[48vw]" />
        <div className="absolute left-[-12vw] top-[-6vw] h-[42vw] w-[42vw] rounded-full bg-blush/40 md:h-[26vw] md:w-[26vw]" />
        <div className="absolute left-[40%] top-[60%] h-[20vw] w-[20vw] rounded-full border-2 border-blush-deep/30 md:h-[12vw] md:w-[12vw]" />
      </div>

      <div className="absolute inset-0">
        {CAKES.map((c) => (
          <FloatingCake key={c.src} cake={c} reduce={reduce} px={px} py={py} progress={scrollYProgress} />
        ))}
      </div>

      <div className="u-container pointer-events-none relative flex h-full flex-col justify-center">
        <p className="eyebrow mb-6 text-sage-deep">
          <motion.span className="inline-block" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            Pasticceria artigianale · Travedona Monate
          </motion.span>
        </p>
        <h1 className="max-w-4xl font-display text-[16vw] leading-[0.86] tracking-tight text-ink md:text-[9vw]">
          <RevealWords text="Dolci fatti" delay={0.35} />
          <span className="block italic text-sage-deep">
            <RevealWords text="a mano" delay={0.6} />
          </span>
        </h1>
        <Reveal delay={1.2} className="mt-7 max-w-md text-xl leading-relaxed text-ink-soft md:text-2xl">
          Torte su ordinazione e dolci freschi pronti, ogni giorno.
        </Reveal>
      </div>
    </section>
  );
}
